import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { equals } from 'ramda';
import { RouteContext } from './route-context';
import { getSearchState, handleNavigation } from './logic';
import { DefaultSearchOptions, Navigate, NavigateOptions, Route } from './types';

const MAX_HASH_SCROLL_SECONDS = 2;
const HASH_SCROLL_CHECK_INTERVAL_MS = 10;

export function RouteProvider({ children }: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const [currentURL, setCurrentURL] = useState<URL>(new URL(window.location.href));
  const [currentHashValue, setCurrentHashValue] = useState(location.hash ? location.hash.slice(1) : '');
  const [prevPathname, setPrevPathname] = useState('');
  const prevSearchOptions = useRef<DefaultSearchOptions>();

  const searchOptions = useMemo(<S extends DefaultSearchOptions>() => {
    const newSearchOptions = getSearchState<S>(currentURL);
    const normPrevSearchOptions = (prevSearchOptions.current as unknown) as S;

    //
    //
    // make sure that the search options truly changed (i.e. maybe the url hash changed but not query)
    //
    //

    if (equals<S>(normPrevSearchOptions, newSearchOptions)) {
      return normPrevSearchOptions;
    }

    prevSearchOptions.current = newSearchOptions;

    return newSearchOptions;
  }, [currentURL]);

  const navigate = useCallback<Navigate>(
    <T extends unknown>(route: Route | string, state?: T, options?: NavigateOptions) => {
      const navResult = handleNavigation<T>({
        origin: currentURL.origin,
        route,
        state,
        options,
      });

      const newUrl = navResult ? navResult[0] : undefined;
      const prevUrl = navResult ? navResult[1] : undefined;

      // since handleNavigation returns a newly constructed URL object
      // make sure it's truly a new url before setting it to avoid
      // unnecessary context re-renders of the virtual DOM
      if (newUrl && newUrl.toString() !== currentURL.toString()) {
        setCurrentURL(newUrl);
        setPrevPathname(prevUrl ? prevUrl.pathname : '');
      }
    },
    [currentURL]
  );

  const setSearchOptions = useCallback(
    <S extends DefaultSearchOptions>(options?: S) => {
      const newURL = new URL(currentURL.toString());

      // clear previous search
      newURL.search = '';

      if (options) {
        //
        // populate new search based on options
        //
        Object.keys(options).forEach((key) => {
          const val = options[key];

          if (Array.isArray(val)) {
            val.forEach((item) => {
              newURL.searchParams.append(key, item);
            });
          } else {
            newURL.searchParams.append(key, String(val));
          }
        });
      }

      if (newURL.search !== currentURL.search) {
        setCurrentURL(newURL);
        window.history.pushState(window.history.state, '', newURL.toString());
      }
    },
    [currentURL]
  );

  //
  // manage popstate listener
  //
  useEffect(() => {
    const listener = () => {
      // circumvent the possibility of a duplicative state
      // push (i.e. same URL) before setting the current URL
      // to avoid unnecessary context re-renders of the virtual DOM
      if (window.location.href !== currentURL.toString()) {
        setCurrentURL(new URL(window.location.href));
      }
    };

    window.addEventListener('popstate', listener);

    return () => {
      window.removeEventListener('popstate', listener);
    };
  }, [currentURL]);

  //
  // manage hash listener
  //
  useEffect(() => {
    const listener = () => {
      setCurrentHashValue(location.hash.slice(1));
    };

    window.addEventListener('hashchange', listener);

    return () => {
      window.removeEventListener('hashchange', listener);
    };
  }, []);

  //
  // manage hash scrolling
  //
  useEffect(() => {
    const maxHashScrollRetries = (MAX_HASH_SCROLL_SECONDS * 1000) / HASH_SCROLL_CHECK_INTERVAL_MS;

    let retries = 0;
    let intervalId = 0;

    if (currentHashValue) {
      intervalId = window.setInterval(() => {
        const element = document.getElementById(currentHashValue);

        if (element || retries > maxHashScrollRetries) {
          window.clearInterval(intervalId);

          if (element) {
            element.scrollIntoView();
          }
        } else {
          retries++;
        }
      }, HASH_SCROLL_CHECK_INTERVAL_MS);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [currentHashValue]);

  return (
    <RouteContext.Provider
      value={{ currentHashValue, currentURL, navigate, prevPathname, searchOptions, setSearchOptions }}
    >
      {children}
    </RouteContext.Provider>
  );
}
